/**
 * ----------------------------------------------------------------------------------------------------------------------
 * VIDEO PRIVACY CONSENT
 * ----------------------------------------------------------------------------------------------------------------------
 */

const config = window.themeVideoConsent;

if (config && config.enabled) {
  const GRANTED_ATTRIBUTE = 'data-video-consent';
  const TIKTOK_SCRIPT_SRC = 'https://www.tiktok.com/embed.js';

  const isGloballyGranted = () => document.documentElement.getAttribute(GRANTED_ATTRIBUTE) === 'granted';

  const isBlocked = (element) => {
    if (isGloballyGranted()) return false;

    return !element.hasAttribute('consent-granted');
  };

  const template = () => document.getElementById('video-consent-template');

  const restoreIframe = (iframe) => {
    const source = iframe.getAttribute('data-disabled-src');

    iframe.removeAttribute('data-disabled-src');

    if (iframe.getAttribute('data-lazy-load') === 'true') {
      iframe.setAttribute('data-src', source);
    } else {
      iframe.setAttribute('src', source);
    }

    iframe.removeAttribute('data-lazy-load');
    iframe.classList.add('consent-checked');
  };

  const release = (gate) => {
    gate.setAttribute('consent-granted', '');
    gate.classList.remove('has-overlay');

    const iframe = gate.querySelector('iframe[data-disabled-src]');

    if (iframe) restoreIframe(iframe);

    const tiktokEmbed = gate.querySelector('.hidden-tiktok-embed');

    if (tiktokEmbed) {
      const embed = document.createElement('div');
      const script = document.createElement('script');

      script.setAttribute('async', 'true');
      script.setAttribute('src', TIKTOK_SCRIPT_SRC);
      embed.innerHTML = tiktokEmbed.textContent;

      tiktokEmbed.remove();
      gate.append(embed, script);
    }

    if (gate.tagName === 'VIDEO-MEDIA' && gate.hasAttribute('autoplay')) {
      gate.play();
    }
  };

  const releaseAll = () => {
    document.querySelectorAll('video-media, [data-video-consent-gate]').forEach((gate) => release(gate));
  };

  customElements.whenDefined('video-media').then(() => {
    const prototype = customElements.get('video-media').prototype;
    const originalPlay = prototype.play;

    prototype.play = function (...args) {
      if (this.querySelector('[data-video-consent-panel]') && isBlocked(this)) return;

      return originalPlay.apply(this, args);
    };
  });

  const gateRichTextEmbeds = (root) => {
    if (!template()) return;

    if (isGloballyGranted()) {
      root.querySelectorAll('iframe[data-disabled-src]').forEach(restoreIframe);
      return;
    }

    root.querySelectorAll('iframe[data-disabled-src]').forEach((iframe) => {
      if (iframe.closest('[data-video-consent-gate]')) return;

      const wrapper = document.createElement('div');

      wrapper.className = 'video-iframe-wrapper has-overlay';
      wrapper.setAttribute('data-video-consent-gate', '');

      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
      wrapper.appendChild(template().content.cloneNode(true));
    });

    root.querySelectorAll('blockquote.tiktok-embed').forEach((embed) => {
      const wrapper = document.createElement('div');

      wrapper.className = 'video-iframe-wrapper has-overlay';
      wrapper.setAttribute('data-video-consent-gate', '');

      const parked = document.createElement('div');

      parked.className = 'hidden-tiktok-embed';
      parked.textContent = embed.outerHTML;

      embed.parentNode.insertBefore(wrapper, embed);
      embed.remove();
      wrapper.appendChild(parked);
      wrapper.appendChild(template().content.cloneNode(true));
    });
  };

  const gateOrphanVideoMedia = (root) => {
    if (!template() || isGloballyGranted()) return;

    root.querySelectorAll('video-media[host]').forEach((element) => {
      if (element.querySelector('[data-video-consent-panel]')) return;

      element.appendChild(template().content.cloneNode(true));
    });
  };

  const scan = (node) => {
    const root = node.nodeType === Node.ELEMENT_NODE && node.parentNode ? node.parentNode : node;

    gateRichTextEmbeds(root);
    gateOrphanVideoMedia(root);
  };

  const accept = () => {
    document.documentElement.setAttribute(GRANTED_ATTRIBUTE, 'granted');
    config.observer?.disconnect();
    config.accept();
    releaseAll();
  };

  document.addEventListener(
    'click',
    (event) => {
      const panel = event.target.closest('[data-video-consent-panel]');

      if (!panel) return;

      event.stopPropagation();

      const acceptsClick = event.target.closest('[data-video-consent-accept]') || event.target === panel;

      if (!acceptsClick) return;

      event.preventDefault();

      accept();
    },
    true,
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:block:select', (event) => scan(event.target));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}
